import type { ReactElement, ReactNode } from 'react';
import {
  Badge,
  Box,
  Divider,
  Flex,
  Group,
  Paper,
  Stack,
  Text,
  Title,
} from '@mantine/core';
import { DateTime } from 'luxon';
import type { Rule } from '@billing/validation/rules';
import type { RuleTranslate } from '../shared/constants';

export const RuleEditLayout = ({
  rule,
  timezone,
  translate,
  preview,
  children,
}: {
  rule: Rule;
  timezone: string;
  translate: RuleTranslate;
  preview: { action: ReactNode; criteria: ReactNode; outcome: ReactNode };
  children: ReactNode;
}): ReactElement => {
  const on = rule.active || rule.status === 'published';
  const edited = rule.lastUpdatedAt || rule.createdAt;
  const date = edited
    ? DateTime.fromISO(edited, { zone: 'utc' }).setZone(timezone)
    : null;
  return (
    <Flex direction={{ base: 'column', md: 'row' }} gap="lg" align="flex-start">
      <Paper
        withBorder
        radius="lg"
        shadow="sm"
        miw={0}
        w="100%"
        style={{ flex: 2, overflow: 'hidden' }}
      >
        <Stack p="lg" gap="xs">
          <Title order={2} size="h4">
            {translate('pages:rules.builder.edit.makeChanges')}
          </Title>
          <Text size="sm" c="dimmed">
            {translate('pages:rules.builder.edit.saveHint')}
          </Text>
        </Stack>
        {children}
      </Paper>
      <Paper
        component="aside"
        aria-label={translate('pages:rules.builder.edit.preview')}
        withBorder
        radius="lg"
        shadow="sm"
        pos={{ base: 'static', md: 'sticky' }}
        top="var(--mantine-spacing-md)"
        w="100%"
        miw={{ base: 0, md: '16rem' }}
        style={{ flex: 1, overflow: 'hidden' }}
      >
        <Stack p="lg" gap="sm">
          <Group justify="space-between">
            <Text size="xs" fw={700} c="dimmed" tt="uppercase">
              {translate('pages:rules.builder.edit.currentRule')}
            </Text>
            <Badge variant="light" color={on ? 'green' : 'gray'}>
              {translate(`pages:rules.builder.${on ? 'edit.on' : 'edit.off'}`)}
            </Badge>
          </Group>
          <Title order={3} size="h4">
            {rule.name}
          </Title>
          <Text size="xs" c="dimmed">
            {translate('pages:rules.builder.edit.version')} {rule.version}
          </Text>
          {date?.isValid && (
            <Text size="xs" c="dimmed">
              {translate('pages:rules.builder.edit.lastEdited')}{' '}
              {date.toFormat('yyyy-LL-dd HH:mm ZZZZ')}
            </Text>
          )}
        </Stack>
        {(['action', 'criteria', 'outcome'] as const).map((key) => (
          <Box key={key}>
            <Divider />
            <Stack p="lg" gap="sm">
              <Text size="xs" fw={700} c="dimmed" tt="uppercase">
                {translate(`pages:rules.builder.steps.${key}`)}
              </Text>
              {preview[key]}
            </Stack>
          </Box>
        ))}
      </Paper>
    </Flex>
  );
};

export const RuleEditSection = ({
  title,
  changed,
  translate,
  children,
}: {
  title: string;
  changed: boolean;
  translate: RuleTranslate;
  children: ReactNode;
}): ReactElement => (
  <Box
    component="section"
    p="lg"
    style={{ borderTop: '1px solid var(--mantine-color-default-border)' }}
    aria-label={title}
  >
    <Group gap="sm" mb="md">
      <Text component="h2" size="md" fw={700} m={0}>
        {title}
      </Text>
      {changed && (
        <Badge size="sm" variant="light" color="blue">
          {translate('pages:rules.builder.edit.edited')}
        </Badge>
      )}
    </Group>
    {children}
  </Box>
);
